import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getUsers, deleteUser, type User } from "../../services/users";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

export default function PlatformAdmins() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [platformAdmins, setPlatformAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    fetchPlatformAdmins();
  }, []);

  const fetchPlatformAdmins = async () => {
    try {
      setLoading(true);
      const response = await getUsers({ role: "platform_admin" });
      setPlatformAdmins(response.data);
    } catch (error) {
      console.error("Failed to fetch platform admins:", error);
      toast.error("Nu s-au putut încărca administratorii platformei");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId: string) => {
    if (deleteConfirmId === userId) {
      // Second click - proceed with delete
      handleDeleteConfirm(userId);
    } else {
      // First click - show confirmation
      setDeleteConfirmId(userId);
      setTimeout(() => setDeleteConfirmId(null), 3000); // Reset after 3 seconds
    }
  };

  const handleDeleteConfirm = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success("Platform Admin șters cu succes!");
      setDeleteConfirmId(null);
      fetchPlatformAdmins();
    } catch (error: any) {
      console.error("Failed to delete platform admin:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut șterge administratorul platformei");
    }
  };

  const filteredAdmins = platformAdmins.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-600 font-medium">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Utilizatori și Membri"
          title="Administratori platformă"
          subtitle={<>Gestionare administratori platformă ({filteredAdmins.length})</>}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={() => navigate("/admin/users")}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Adaugă administrator nou
            </AdminButton>
          }
        />

        {/* Search */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Caută platform admin după nume sau email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>

        {/* Platform Admins Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Nume
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Creat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAdmins.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>Nu există administratori de platformă înregistrați</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAdmins.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      {/* Name - Clickable */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
                        >
                          {user.full_name || "-"}
                        </button>
                      </td>

                      {/* Email */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-slate-700 text-sm">{user.email}</span>
                      </td>

                      {/* Created At */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-slate-500 text-sm">
                          {formatDate(new Date(user.created_at))}
                        </span>
                      </td>

                      {/* Actions - Delete with double confirmation.
                          Hide the action on the current admin's own row —
                          they can't (and shouldn't be able to) revoke
                          themselves from this screen. */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {currentUser?.id === user.id ? (
                          <span className="text-xs italic text-slate-400">— contul tău —</span>
                        ) : (
                          <button
                            onClick={() => handleDeleteClick(user.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              deleteConfirmId === user.id
                                ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-500/25"
                                : "bg-red-50 text-red-600 hover:bg-red-100"
                            }`}
                          >
                            {deleteConfirmId === user.id ? "Confirmă Ștergere" : "Șterge"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
